import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';

/**
 * Settings > Notifications (admins): the Microsoft Teams connection.
 * The CRM side is fully built - alerts post automatically once the cloud team
 * creates the Power Automate webhook(s) and pastes the URL(s) here.
 *
 * 14 October 2026: moved here from Integrations. Which notifications reach a
 * person as a Teams message is now their own choice (My notifications, above
 * on the same page), so the company-wide list of message types is gone from
 * this screen; the server keeps the old list only as everyone's starting
 * Teams ticks, and a save here never sends it. The ticks each channel webhook
 * had went too: what is posted to the channels is the Teams channel column of
 * Company settings, so this page is the one place notifications are set.
 */
function TeamsSettings() {
  const { authFetch } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  // Nothing here saves until "Save Teams settings" is pressed (the Test
  // buttons work on unsaved values), so guard against silently losing edits.
  const { guardElement, markDirty, resetDirty } = useUnsavedChanges();
  const [hasChanges, setHasChanges] = useState(false);

  const editSettings = useCallback((updater) => {
    setSettings(updater);
    markDirty();
    setHasChanges(true);
  }, [markDirty]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/integrations/teams');
      if (!response.ok) throw new Error('Failed to load Teams settings');
      const data = await response.json();
      setSettings({
        enabled: Boolean(data.enabled),
        app_base_url: data.app_base_url || 'http://localhost:3000',
        // Only the connection: the old per-channel ticks are no longer kept or sent.
        webhooks: (data.webhooks || []).map(({ name, url }) => ({ name, url })),
        dm_enabled: Boolean(data.dm_enabled),
        dm_url: data.dm_url || ''
      });
      resetDirty();
      setHasChanges(false);
    } catch (err) {
      setError(err.message);
    }
  }, [authFetch, resetDirty]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateWebhook = (index, patch) => {
    editSettings(prev => ({
      ...prev,
      webhooks: prev.webhooks.map((w, i) => (i === index ? { ...w, ...patch } : w))
    }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await authFetch('/api/v1/integrations/teams', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save');
      setSettings({
        enabled: data.enabled,
        app_base_url: data.app_base_url,
        webhooks: (data.webhooks || []).map(({ name, url }) => ({ name, url })),
        dm_enabled: Boolean(data.dm_enabled),
        dm_url: data.dm_url || ''
      });
      resetDirty();
      setHasChanges(false);
      setMessage('Teams notification settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (url, label) => {
    setTesting(label);
    setError(null);
    setMessage(null);
    try {
      const response = await authFetch('/api/v1/integrations/teams/test', {
        method: 'POST',
        body: JSON.stringify(url ? { url } : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Test failed');
      setMessage(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(null);
    }
  };

  const sendDmTest = async () => {
    setTesting('dm');
    setError(null);
    setMessage(null);
    try {
      const response = await authFetch('/api/v1/integrations/teams/test-dm', {
        method: 'POST',
        body: JSON.stringify(settings.dm_url ? { url: settings.dm_url } : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Test failed');
      setMessage(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(null);
    }
  };

  if (!settings) {
    return <div className="settings-section">{error || 'Loading Teams settings...'}</div>;
  }

  return (
    <div className="settings-section teams-settings">
      <h2>Microsoft Teams connection</h2>
      <p className="settings-hint">
        Admins only. Connects the CRM to Teams: personal messages to each person, and posts into Teams
        channels, as cards with an "Open in IDS CRM" button. Which notifications each person gets as a Teams
        message is their own choice under My notifications.
      </p>

      <div className="teams-cloud-todo">
        <strong>Cloud team setup (per channel):</strong>
        <ol>
          <li>In Power Automate, create a workflow from the template <em>"Post to a channel when a webhook request is received"</em> for the target team/channel.</li>
          <li>Copy the workflow's HTTP URL and paste it as a webhook below (use the Test button to confirm the card arrives).</li>
          <li>Tick <em>Enable Teams notifications</em> and save. Also set the App base URL to the CRM's public address so card links open correctly.</li>
        </ol>
        Nothing needs deploying on the CRM side - alerts start posting as soon as this is enabled.
      </div>

      {error && <div className="admin-error">{error}</div>}
      {message && <div className="admin-info">{message}</div>}

      <div className="form-group teams-toggle">
        <label>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={e => editSettings(prev => ({ ...prev, enabled: e.target.checked }))}
          />
          Enable Teams notifications
        </label>
      </div>

      <div className="form-group">
        <label>App base URL (used for card links)</label>
        <input
          type="text"
          value={settings.app_base_url}
          onChange={e => editSettings(prev => ({ ...prev, app_base_url: e.target.value }))}
          placeholder="https://crm.ids-dairy.com"
        />
      </div>

      <h3>Personal notifications (direct messages)</h3>
      <p className="settings-hint">
        Sends each person the notifications they ticked for Teams as a direct message from the
        "Workflows" bot. Uses one
        Power Automate flow — template <em>"Send webhook alerts to a chat"</em> with
        "Who can trigger" set to Anyone, "Post in" set to Chat with Flow bot, and Recipient
        set to the expression <code>triggerBody()?['recipient_email']</code>. CRM emails must
        match users' Microsoft 365 emails (they do — sign-in is Microsoft SSO).
      </p>
      <div className="form-group teams-toggle">
        <label>
          <input
            type="checkbox"
            checked={settings.dm_enabled}
            onChange={e => editSettings(prev => ({ ...prev, dm_enabled: e.target.checked }))}
          />
          Enable personal Teams notifications
        </label>
      </div>
      <div className="teams-webhook-row">
        <input
          type="text"
          className="teams-webhook-url"
          placeholder="https://... (personal notifications flow URL)"
          value={settings.dm_url}
          onChange={e => editSettings(prev => ({ ...prev, dm_url: e.target.value }))}
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={!settings.dm_url || testing !== null}
          onClick={sendDmTest}
        >
          {testing === 'dm' ? 'Sending...' : 'Test (DMs you)'}
        </button>
      </div>
      <h3>Channel webhooks</h3>
      <p className="settings-hint">
        Each Teams channel connected here receives the notifications ticked in the Teams channel column of
        Company settings above.
      </p>
      {settings.webhooks.length === 0 && (
        <p className="settings-hint">No webhooks yet - add one per Teams channel that should receive alerts.</p>
      )}
      {settings.webhooks.map((webhook, index) => (
        <div key={index} className="teams-webhook-card">
          <div className="teams-webhook-row">
            <input
              type="text"
              placeholder="Name (e.g. Support Alerts channel)"
              value={webhook.name || ''}
              onChange={e => updateWebhook(index, { name: e.target.value })}
            />
            <input
              type="text"
              className="teams-webhook-url"
              placeholder="https://... (Power Automate workflow URL from the cloud team)"
              value={webhook.url || ''}
              onChange={e => updateWebhook(index, { url: e.target.value })}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={!webhook.url || testing !== null}
              onClick={() => sendTest(webhook.url, webhook.name || `webhook ${index + 1}`)}
            >
              {testing === (webhook.name || `webhook ${index + 1}`) ? 'Sending...' : 'Test'}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => editSettings(prev => ({ ...prev, webhooks: prev.webhooks.filter((_, i) => i !== index) }))}
            >
              Remove
            </button>
          </div>
        </div>
      ))}

      <div className="teams-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => editSettings(prev => ({ ...prev, webhooks: [...prev.webhooks, { name: '', url: '' }] }))}
        >
          + Add webhook
        </button>
      </div>

      {/* One save for everything above (general, personal DMs, channel
          webhooks) - kept in its own bar so it doesn't read as belonging to
          the last subsection heading. */}
      <div className="teams-save-bar">
        <button type="button" className="btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving...' : 'Save Teams settings'}
        </button>
        {hasChanges
          ? <span className="teams-unsaved-hint">Unsaved changes — nothing above takes effect until saved.</span>
          : <span className="settings-hint">Saves all Teams notification settings above.</span>}
      </div>
      {guardElement}
    </div>
  );
}

export default TeamsSettings;
